import { useMemo } from 'react';
import { Layout } from '../../components/Layout';
import Content from './Content';
import { useSelector, useDispatch } from 'react-redux';
import {
  selectSubscriptionSummaries,
  selectIsLoadingSubscriptionSummaries,
  selectActiveCluster,
  setActiveClusterId,
  setActiveSubscriptionId,
} from '../../store/slices/subscription';
import { useNavigate } from 'react-router-dom';
import { useGetSubscriptionSummariesByUserIdQuery } from '../../store/services/api';
import { selectUserId } from '../../store/slices/user';
import useWidthBreakpoint from '../../hooks/useWidthBreakpoint';
import { Transaction } from './types';
import type { SubscriptionSummary } from '../../types/subscription';

/**
 * Derives every aggregate the Overview screen renders from the raw
 * subscription summaries. Pure `useMemo` chain — no side effects.
 */
const useOverviewClusterData = () => {
  const data = useSelector(selectSubscriptionSummaries);
  const isLoading = useSelector(selectIsLoadingSubscriptionSummaries);
  const activeCluster = useSelector(selectActiveCluster);

  const activeClusterSubscriptions = useMemo<SubscriptionSummary[]>(() => {
    return data
      .filter((s) =>
        activeCluster === null ? true : s.clusterId === activeCluster,
      )
      .sort((a, b) => b.totalBalanceInStable - a.totalBalanceInStable);
  }, [activeCluster, data]);

  const totalValueByCluster = useMemo(() => {
    if (!data.length) return {} as Record<string, number>;
    return data.reduce<Record<string, number>>((acc, sub) => {
      acc[sub.clusterId] = (acc[sub.clusterId] || 0) + sub.totalBalanceInStable;
      return acc;
    }, {});
  }, [data]);

  const costBasisByCluster = useMemo(() => {
    if (!data.length) return {} as Record<string, number>;
    return data.reduce<Record<string, number>>((acc, sub) => {
      acc[sub.clusterId] =
        (acc[sub.clusterId] || 0) + (sub.profits?.costBasis.stable || 0);
      return acc;
    }, {});
  }, [data]);

  const totalStableProfitByCluster = useMemo(() => {
    return Object.keys(totalValueByCluster).reduce<Record<string, number>>(
      (acc, clusterId) => {
        const cb = costBasisByCluster[clusterId];
        acc[clusterId] = cb ? (totalValueByCluster[clusterId] - cb) / cb : 0;
        return acc;
      },
      {},
    );
  }, [costBasisByCluster, totalValueByCluster]);

  const entirePortfolioValue = useMemo(
    () =>
      Object.values(totalValueByCluster).reduce((acc, curr) => acc + curr, 0),
    [totalValueByCluster],
  );

  const entirePortfolioProfit = useMemo(() => {
    const totalCostBasis = Object.values(costBasisByCluster).reduce(
      (acc, curr) => acc + curr,
      0,
    );
    if (totalCostBasis === 0) return 0;
    return (entirePortfolioValue - totalCostBasis) / totalCostBasis;
  }, [costBasisByCluster, entirePortfolioValue]);

  const activeClusterTotalValue = useMemo(() => {
    return activeCluster === null
      ? entirePortfolioValue
      : totalValueByCluster[activeCluster];
  }, [activeCluster, entirePortfolioValue, totalValueByCluster]);

  const hasPositiveProfit = useMemo<boolean | undefined>(() => {
    if (!data) return undefined;
    if (!activeCluster) {
      if (entirePortfolioProfit === 0) return undefined;
      return entirePortfolioProfit > 0;
    }
    return totalStableProfitByCluster[activeCluster] >= 0;
  }, [activeCluster, data, entirePortfolioProfit, totalStableProfitByCluster]);

  const transactions = useMemo<Transaction[]>(
    () =>
      activeClusterSubscriptions
        .map((s) => s.latestTransaction)
        .filter((t): t is Transaction => !!t)
        .sort(
          (a, b) =>
            (b.transactionTimestamp || 0) - (a.transactionTimestamp || 0),
        ),
    [activeClusterSubscriptions],
  );

  const hasData = !!Object.keys(totalValueByCluster).length;
  const hasMultipleClusters = Object.keys(totalValueByCluster).length > 1;

  return {
    activeCluster,
    activeClusterSubscriptions,
    totalValueByCluster,
    totalStableProfitByCluster,
    entirePortfolioValue,
    entirePortfolioProfit,
    activeClusterTotalValue,
    hasPositiveProfit,
    transactions,
    isLoading,
    hasData,
    hasMultipleClusters,
  };
};

const Overview: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector(selectUserId);
  const { isMatch } = useWidthBreakpoint();

  useGetSubscriptionSummariesByUserIdQuery(userId, {
    refetchOnMountOrArgChange: true,
  });

  const {
    activeCluster,
    activeClusterSubscriptions,
    totalValueByCluster,
    totalStableProfitByCluster,
    entirePortfolioValue,
    entirePortfolioProfit,
    activeClusterTotalValue,
    hasPositiveProfit,
    transactions,
    isLoading,
    hasData,
    hasMultipleClusters,
  } = useOverviewClusterData();

  const handleSubscriptionClick = (subscriptionId: number) => {
    dispatch(setActiveSubscriptionId(subscriptionId));
    navigate('sub/');
  };

  return (
    <Layout hasPositiveProfit={hasPositiveProfit}>
      {hasData ? (
        <Layout.SideMenu hasPositiveProfit={hasPositiveProfit}>
          <>
            <Layout.SideMenuSection>
              {isMatch('md') ? (
                <>
                  {hasMultipleClusters ? (
                    <>
                      <Layout.SideMenuTitle title="Clusters" />
                      <Layout.SideMenuItem
                        id={'Portfolio'}
                        label={'Portfolio'}
                        value={entirePortfolioValue}
                        delta={entirePortfolioProfit}
                        onClick={() => dispatch(setActiveClusterId(null))}
                        isActive={activeCluster === null}
                      />
                    </>
                  ) : (
                    <Layout.SideMenuTitle title="Portfolio" />
                  )}
                  {Object.entries(totalValueByCluster).map(
                    ([clusterId, value]) => (
                      <Layout.SideMenuItem
                        key={clusterId}
                        id={clusterId}
                        label={clusterId}
                        value={value}
                        delta={totalStableProfitByCluster[clusterId]}
                        onClick={(clusterId: string) =>
                          dispatch(setActiveClusterId(clusterId))
                        }
                        isActive={activeCluster === clusterId}
                      />
                    ),
                  )}
                </>
              ) : (
                <>
                  <Layout.SideMenuTitle title="Active Cluster" />
                </>
              )}
            </Layout.SideMenuSection>

            {isMatch('md') && activeClusterSubscriptions?.length ? (
              <Layout.SideMenuSection>
                <Layout.SideMenuTitle title="Subscriptions" />
                {activeClusterSubscriptions.map((sub) => (
                  <Layout.SideMenuItem
                    key={sub.id}
                    id={sub.id}
                    label={sub.botFriendlyName}
                    value={sub.totalBalanceInStable}
                    delta={sub.currentProfit}
                    onClick={handleSubscriptionClick}
                  />
                ))}
              </Layout.SideMenuSection>
            ) : undefined}
          </>
        </Layout.SideMenu>
      ) : undefined}

      <Layout.Content>
        <Content
          subscriptions={activeClusterSubscriptions}
          totalValue={activeClusterTotalValue}
          transactions={transactions}
          isLoading={isLoading}
        />
      </Layout.Content>
    </Layout>
  );
};

export default Overview;
